import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { ApiError, type Note } from "../api/types";
import { Button, TextField } from "../compose/controls";
import { FileText, PenLine, Plus } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import {
  EmptyState,
  ErrorState,
  SkeletonRows,
  Spinner,
} from "../compose/states";
import { actorFromLegacy } from "../lib/avatar";
import { formatDate } from "../lib/format";
import { stripMarkdown } from "../lib/markdownText";
import { useAsync } from "../lib/useAsync";
import { useElementRef } from "../lib/useElementRef";
import { Avatar } from "../components/shared/Avatar";

/**
 * Notes `/w/:slug/notes` (ia-and-flows §1 — "the institutional knowledge").
 * Per-subject research notes: agents write them, humans read + organize. A note
 * is markdown, optionally scoped to a document (`?doc=<id>`). Two-pane: a list of
 * notes on the left, a title + markdown editor with a server-rendered preview on
 * the right. The preview paints the backend's `body_html` (same md-render path
 * documents use) so "what you write is what you read".
 */
export function Notes() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const docScope = params.get("doc"); // optional document filter/attachment
  const toast = useToast();

  const notes = useAsync(
    () => api.listNotes(slug, docScope ?? undefined),
    [slug, docScope],
  );

  // The editor holds either an existing note (id set) or a fresh draft ("new").
  interface Draft {
    id: string | "new";
    title: string;
    body: string;
    html: string;
  }
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadingNote, setLoadingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  // Paint the server-rendered HTML imperatively (a ref, not
  // dangerouslySetInnerHTML) — the same technique the document canvas uses.
  //
  // The preview element is CONDITIONALLY MOUNTED (`draft.html ? … : …`), so it is
  // destroyed and recreated as the reviewer moves between notes. `element` leads
  // the dependency list for the same reason it does in `useDoczCanvas`: a paint
  // keyed only on content silently skips a freshly mounted node whenever the
  // content happens to be unchanged, and the preview then stays blank forever.
  const { ref: previewRef, element: previewEl } = useElementRef<HTMLDivElement>();
  useEffect(() => {
    if (previewEl) previewEl.innerHTML = draft?.html ?? "";
  }, [previewEl, draft?.html, draft?.id]);

  const openNote = useCallback(async (id: string) => {
    setLoadingNote(true);
    try {
      const note: Note = await api.getNote(id);
      setDraft({
        id: String(note.id),
        title: note.title,
        body: note.body,
        html: note.body_html,
      });
    } catch {
      toast.show("Couldn't open that note.");
    } finally {
      setLoadingNote(false);
    }
  }, [toast]);

  const startNew = useCallback(() => {
    setDraft({ id: "new", title: "", body: "", html: "" });
  }, []);

  const save = useCallback(async () => {
    if (!draft || draft.title.trim().length === 0) {
      toast.show("A note needs a title.");
      return;
    }
    setSaving(true);
    try {
      const saved =
        draft.id === "new"
          ? await api.createNote(slug, {
              title: draft.title,
              body: draft.body,
              document_id: docScope ? Number(docScope) : null,
            })
          : await api.updateNote(draft.id, {
              title: draft.title,
              body: draft.body,
            });
      setDraft({
        id: String(saved.id),
        title: saved.title,
        body: saved.body,
        html: saved.body_html,
      });
      notes.reload();
      toast.show(draft.id === "new" ? "Note created." : "Note saved.");
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 400
          ? "That note couldn't be saved (check the attached document)."
          : "Saving the note failed.";
      toast.show(msg);
    } finally {
      setSaving(false);
    }
  }, [draft, slug, docScope, notes, toast]);

  const forbidden =
    notes.error instanceof ApiError &&
    (notes.error.status === 403 || notes.error.status === 404);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Notes</h1>
          <p className="page__sub">
            {docScope
              ? "Research notes attached to this document."
              : "Per-subject research notes — the institutional knowledge for this workspace."}
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="notes-new"
          icon={<Plus size={15} strokeWidth={1.5} />}
          onClick={startNew}
        >
          New note
        </Button>
      </div>

      <div
        className="notes-layout"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(240px, 340px) 1fr",
          gap: "var(--norkron-space-4, 20px)",
          alignItems: "start",
        }}
      >
        {/* ── list column ──────────────────────────────────────────── */}
        <div>
          {notes.loading ? (
            <SkeletonRows rows={4} />
          ) : forbidden ? (
            <EmptyState
              icon={<PenLine size={28} strokeWidth={1.5} />}
              title="No access"
              body="You don't have access to notes in this workspace."
            />
          ) : notes.error ? (
            <ErrorState body="We couldn't load the notes." />
          ) : notes.data && notes.data.length > 0 ? (
            <div className="notes-list" data-testid="notes-list">
              {notes.data.map((n) => {
                const active = draft?.id === String(n.id);
                // The backend excerpt is raw markdown; show a clean plain-text
                // preview, never literal `#`/`**`/`` ` `` syntax on the card.
                const preview = stripMarkdown(n.excerpt);
                return (
                  <button
                    type="button"
                    key={n.id}
                    className={`card note-card${active ? " note-card--active" : ""}`}
                    data-testid={`note-card-${n.id}`}
                    aria-pressed={active}
                    onClick={() => void openNote(String(n.id))}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      cursor: "pointer",
                      marginBottom: 10,
                      outline: active
                        ? "2px solid var(--ela-accent, #5b8def)"
                        : undefined,
                    }}
                  >
                    <div
                      className="note-card__title"
                      style={{ fontWeight: 600, marginBottom: 4 }}
                    >
                      {n.title || "Untitled note"}
                    </div>
                    {preview ? (
                      <div
                        className="note-card__excerpt"
                        style={{
                          color: "var(--norkron-text-muted)",
                          fontSize: 13,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {preview}
                      </div>
                    ) : null}
                    <div
                      className="note-card__meta"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 8,
                        fontSize: 12,
                        color: "var(--norkron-text-muted)",
                      }}
                    >
                      <Avatar
                        actor={n.author ?? actorFromLegacy(n.author_name)}
                        size="sm"
                      />
                      <span>{n.author_name}</span>
                      <span aria-hidden>·</span>
                      <span>{formatDate(n.updated_at)}</span>
                      {n.document_id != null ? (
                        <span
                          className="badge badge--neutral"
                          style={{ marginLeft: "auto" }}
                        >
                          <FileText size={11} strokeWidth={1.5} /> Doc
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={<PenLine size={28} strokeWidth={1.5} />}
              title="No notes yet"
              body={
                docScope
                  ? "No notes are attached to this document. Create the first one."
                  : "Capture the first research note for this workspace."
              }
              action={
                <Button variant="secondary" onClick={startNew}>
                  New note
                </Button>
              }
            />
          )}
        </div>

        {/* ── editor / preview column ──────────────────────────────── */}
        <div className="note-editor" data-testid="note-editor">
          {loadingNote ? (
            <div style={{ paddingTop: 40 }}>
              <Spinner label="Opening note…" />
            </div>
          ) : draft ? (
            <div className="card" style={{ padding: 20 }}>
              <TextField
                label="Title"
                data-testid="note-title-input"
                value={draft.title}
                placeholder="Note title"
                onChange={(e) =>
                  setDraft({ ...draft, title: e.currentTarget.value })
                }
              />
              <div className="field">
                <label className="field__label" htmlFor="note-body">
                  Body (markdown)
                </label>
                <textarea
                  id="note-body"
                  className="field__input"
                  data-testid="note-body-input"
                  value={draft.body}
                  placeholder={"# Heading\n\nWrite the research note in markdown…"}
                  onChange={(e) =>
                    setDraft({ ...draft, body: e.currentTarget.value })
                  }
                  rows={10}
                  style={{
                    resize: "vertical",
                    minHeight: 180,
                    fontFamily: "var(--norkron-font-mono, monospace)",
                  }}
                />
              </div>
              <div
                style={{ display: "flex", gap: 10, margin: "6px 0 18px" }}
              >
                <Button
                  variant="primary"
                  data-testid="note-save"
                  disabled={saving || draft.title.trim().length === 0}
                  onClick={() => void save()}
                >
                  {saving
                    ? "Saving…"
                    : draft.id === "new"
                      ? "Create note"
                      : "Save changes"}
                </Button>
                <Button
                  variant="ghost"
                  data-testid="note-close"
                  onClick={() => setDraft(null)}
                >
                  Close
                </Button>
              </div>

              <div
                className="note-preview__label"
                style={{
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--norkron-text-muted)",
                  marginBottom: 8,
                }}
              >
                Preview
              </div>
              {draft.html ? (
                <div
                  ref={previewRef}
                  className="ela-doc note-preview"
                  data-testid="note-preview"
                />
              ) : (
                <p
                  data-testid="note-preview-empty"
                  style={{ color: "var(--norkron-text-muted)", fontSize: 14 }}
                >
                  Save the note to see it rendered.
                </p>
              )}
            </div>
          ) : (
            <EmptyState
              icon={<PenLine size={30} strokeWidth={1.5} />}
              title="Pick a note to read"
              body="Select a note from the list, or create a new one to capture research."
            />
          )}
        </div>
      </div>
    </div>
  );
}
